/**
 * DocPipeline — runs the 4 pre-processing stages in order:
 *
 *   localize → bgremove → deskew → orient   (port of preprocess.py's chain)
 *
 * Returns the final image, a per-stage {name, method} trace, and a manifest
 * (the orientation/rotation summary the trainer writes to
 * orientation_manifest.json). Per-document overrides (skip / rotate / exclude)
 * are honoured exactly like pipeline_common.py.
 *
 * All ONNX/tesseract work is behind the {@link PipelineAdapters} ports, so the
 * pixel-processing core stays framework-agnostic and Lambda-compatible.
 */
import { ImagePixels } from '../core/ports';
import { cloneImage, rotate90 } from './core/image';
import { localize } from './stages/localize';
import { removeBackground } from './stages/bgremove';
import { deskew } from './stages/deskew';
import { orientUpright } from './stages/orient';
import { PipelineAdapters } from './ports';
import {
  OverrideContext,
  StageName,
  isSkipped,
  isExcluded,
  rotateOverride,
} from './overrides';

export interface StageTrace {
  name: StageName;
  /** The method label that stage chose (or 'skip' when overridden). */
  method: string;
  /** This stage's output image — only populated when ProcessOptions.debug is set. */
  image?: ImagePixels;
}

export interface DocManifest {
  /** Cardinal rotation the orient stage applied (CW degrees). */
  orientMethod: string;
  /** Manual reviewer rotation applied on top (CW degrees, 0 if none). */
  manualRotate: number;
  /** Whether this doc is flagged exclude (training-only; passed through). */
  exclude: boolean;
}

export interface DocResult {
  image: ImagePixels;
  stages: StageTrace[];
  manifest: DocManifest;
}

export interface ProcessOptions extends OverrideContext {
  adapters?: PipelineAdapters;
  /** When true, each StageTrace carries that stage's output image (for the UI). */
  debug?: boolean;
}

/**
 * Process a single document image through the full pipeline. Stages whose name
 * appears in the override `skip` list are passed through untouched (method
 * 'skip'). The manual `rotate` override is applied after orientation.
 */
export async function processDocument(
  image: ImagePixels,
  opts: ProcessOptions = {},
): Promise<DocResult> {
  const adapters = opts.adapters ?? {};
  const stages: StageTrace[] = [];
  let cur = cloneImage(image);
  // Capture each stage's output image when debugging, so the UI can show the
  // pipeline step by step.
  const trace = (name: StageName, method: string) => {
    const t: StageTrace = { name, method };
    if (opts.debug) t.image = cloneImage(cur);
    stages.push(t);
  };

  // 01 — localize
  if (isSkipped(opts, 'localize')) {
    trace('localize', 'skip');
  } else {
    const r = localize(cur);
    cur = r.image;
    trace('localize', r.method);
  }

  // 02 — bgremove
  if (isSkipped(opts, 'bgremove')) {
    trace('bgremove', 'skip');
  } else {
    const r = await removeBackground(cur, adapters.segmentation, adapters.faceDetector);
    cur = r.image;
    trace('bgremove', r.method);
  }

  // 03 — deskew
  if (isSkipped(opts, 'deskew')) {
    trace('deskew', 'skip');
  } else {
    const r = deskew(cur);
    cur = r.image;
    trace('deskew', r.method);
  }

  // 04 — orient (+ manual rotation override on top)
  let orientMethod = 'skip';
  if (isSkipped(opts, 'orient')) {
    trace('orient', 'skip');
  } else {
    const r = await orientUpright(cur, adapters.faceDetector, adapters.osd);
    cur = r.image;
    orientMethod = r.method;
    trace('orient', r.method);
  }

  const manual = rotateOverride(opts);
  if (manual === 90 || manual === 180 || manual === 270) {
    cur = rotate90(cur, manual);
  }

  return {
    image: cur,
    stages,
    manifest: {
      orientMethod,
      manualRotate: manual,
      exclude: isExcluded(opts),
    },
  };
}

/**
 * Thin OO wrapper for callers that want to construct once (with adapters) and
 * reuse. Mirrors the lib's other class adapters.
 */
export class DocPipeline {
  constructor(private readonly adapters: PipelineAdapters = {}) {}

  process(image: ImagePixels, overrides: OverrideContext = {}, debug = false): Promise<DocResult> {
    return processDocument(image, { ...overrides, adapters: this.adapters, debug });
  }
}
